// The sheets as slides: making, duplicating and deleting a sheet (a room's
// lighting sheet too), adding a viewport, the bottom view tabs, the sheet strip
// with its thumbnails, reordering, renaming and a sheet's tab menu.
// docs/claude/sheets.md.
import {
  type Discipline,
  DISCIPLINE_KINDS,
  type ProductInstance,
  type Sheet,
} from '../../shared/types';
import {
  drawingRev,
  invalidate,
  newId,
  pushHistory,
  redrawBackdrop,
  renderProperties,
  state,
} from './core';
import {
  getUiLang,
  tr,
  trIn,
} from '../i18n';
import { resolvedOutline } from '../polyRoom';
import { rcpTitlePos } from '../rcp';
import {
  applyPaper,
  ensureScheduleRects,
} from './sheetPanel';
import {
  copiedSheetName,
  fitViewportToBounds,
  makeSheet,
  makeViewport,
  nextSheetNum,
  SHEET_SCALES,
  sheetName,
} from '../sheets';
import { renderMasterBar } from './titleEditor';
import { closeView3d } from './view3d';
import {
  $,
  defaultCartiglioId,
  hideCtxMenu,
  rcpViewOutline,
} from '../main';
import {
  activeSheet,
  clampViewportToContent,
  drawingSheets,
  fitDefaultViewport,
  modelBoundsOrNull,
  renderSheetToCanvas,
  roomPlanLabel,
  setSchemSel,
  setViewTab,
  sheetLabel,
  sheetWhiteArea,
} from './sheetSpace';
import { elecViewsGo } from './elecSheet';
import { instanceToWorld } from '../geometry';

function createSheet(): void {
  pushHistory();
  const sheets = drawingSheets();
  const sh = makeSheet();
  sh.num = nextSheetNum(sheets);
  sh.cartiglioId = defaultCartiglioId();
  const vp = makeViewport(sh, modelBoundsOrNull());
  fitDefaultViewport(vp, sh);
  sh.viewports.push(vp);
  sheets.push(sh);
  setViewTab(sh.id);
}

/** The scales a lighting plan sheet's viewport is set to (1 : n). */
const LIGHTING_SHEET_SCALES = [10, 20, 25, 50, 75, 100, 200, 500];

/** A room's lighting plan, one undo step: its reflected ceiling plan (one put
 *  beside it, held on its projection, when it has none) and a new sheet that
 *  shows Lighting only — one viewport fitted to the room and that view, left of
 *  the Lighting table, which the sheet shows. */
export function createLightingSheet(room: ProductInstance): void {
  pushHistory();
  const outline = resolvedOutline(room, 0).map((p) => instanceToWorld(room, p));
  const xs = outline.map((p) => p.x);
  let view = (state.drawing.ceilingPlans ?? []).find((v) => v.roomId === room.id);
  if (!view) {
    view = { id: newId('rcp'), roomId: room.id, offset: { x: Math.round(Math.max(...xs) - Math.min(...xs) + 1000), y: 0 } };
    (state.drawing.ceilingPlans ??= []).push(view);
  }
  const ring = rcpViewOutline(view) ?? [], title = rcpTitlePos(view, ring);
  const both = [...outline, ...ring, ...(title ? [{ x: title.x, y: title.y - 300 }] : [])];
  const sheets = drawingSheets();
  const sh = makeSheet();
  sh.lightingRoom = room.id;
  sh.num = nextSheetNum(sheets, room.id);
  sh.cartiglioId = defaultCartiglioId();
  sh.disciplines = Object.fromEntries(DISCIPLINE_KINDS.map((d) => [d, d === 'lighting'])) as Record<Discipline, boolean>;
  sh.showSchedules = true;
  ensureScheduleRects(sh);
  const vp = makeViewport(sh, null), area = sheetWhiteArea(sh), table = sh.lightingScheduleRect!;
  vp.x = area.x0; vp.y = area.y0;
  vp.w = Math.max(20, table.x - 6 - area.x0); vp.h = Math.max(20, area.y1 - area.y0);
  fitViewportToBounds(vp, { min: { x: Math.min(...both.map((p) => p.x)), y: Math.min(...both.map((p) => p.y)) }, max: { x: Math.max(...both.map((p) => p.x)), y: Math.max(...both.map((p) => p.y)) } });
  // The fit's scale taken out to the next scale a drawing is printed at.
  const den = LIGHTING_SHEET_SCALES.find((n) => n >= 1 / vp.scale - 1e-6);
  if (den) vp.scale = 1 / den;
  sh.viewports.push(vp);
  sheets.push(sh);
  setViewTab(sh.id);
}

/** Make an identical copy of a sheet — same paper/orientation/title-block
 *  assignment and a deep copy of its viewports (new ids) — inserted right
 *  after the original and switched to. */
function duplicateSheet(sh: Sheet): void {
  pushHistory();
  const sheets = drawingSheets();
  const copy: Sheet = JSON.parse(JSON.stringify(sh)) as Sheet;
  copy.id = newId('sheet');
  delete copy.name; delete copy.num;
  Object.assign(copy, copiedSheetName(sh, sheets));
  copy.viewports = copy.viewports.map((v) => ({ ...v, id: newId('vp') }));
  const i = sheets.findIndex((s) => s.id === sh.id);
  sheets.splice(i + 1, 0, copy);
  setViewTab(copy.id);
}

export function deleteSheet(id: string): void {
  const sheets = drawingSheets();
  const i = sheets.findIndex((s) => s.id === id);
  if (i < 0) return;
  pushHistory();
  elecViewsGo(sheets[i].elecViews ?? []);
  sheets.splice(i, 1);
  if (state.viewTab === id) { state.viewTab = 'model'; closeView3d(); document.body.classList.remove('sheet-mode'); }
  renderViewTabs();
  renderProperties();
  redrawBackdrop();
  invalidate();
}

export function addViewport(): void {
  const sh = activeSheet(); if (!sh) return;
  pushHistory();
  const v = makeViewport(sh, modelBoundsOrNull());
  v.x += 10; v.y += 10; v.w = Math.min(v.w, sh.widthMm * 0.5); v.h = Math.min(v.h, sh.heightMm * 0.5);
  clampViewportToContent(v, sh);
  sh.viewports.push(v);
  setSchemSel({ kind: 'viewport', id: v.id });
  renderProperties();
  invalidate();
}

/** Build the bottom-left tab bar: Model · 3D · sheets · +. */
export function renderViewTabs(): void {
  const bar = $('view-tabs');
  bar.innerHTML = '';
  const tab = (id: string, label: string, onClick: () => void, onDbl?: () => void): HTMLElement => {
    const el = document.createElement('button');
    el.className = 'view-tab' + (state.viewTab === id ? ' active' : '');
    el.textContent = label;
    el.addEventListener('click', onClick);
    if (onDbl) el.addEventListener('dblclick', onDbl);
    bar.appendChild(el);
    return el;
  };
  tab('model', tr('Model'), () => setViewTab('model'));
  tab('3d', tr('3D'), () => setViewTab('3d'));
  drawingSheets().forEach((s, i) => {
    if (s.id === renamingSheetId) { bar.appendChild(sheetRenameInput(s)); return; }
    const el = tab(s.id, sheetLabel(s), () => setViewTab(s.id), () => renameSheet(s));
    el.addEventListener('contextmenu', (e) => { e.preventDefault(); showSheetTabMenu(e, s); });
    makeSheetDraggable(el, s.id, i, 'x');
  });
  const add = document.createElement('button');
  add.className = 'view-tab view-tab-add';
  add.textContent = '＋';
  add.title = tr('Add a sheet');
  add.addEventListener('click', createSheet);
  bar.appendChild(add);
  renderSheetStrip();
  renderMasterBar();
}

/** Move a sheet to position `to` (its index after the move) — the thumbnail
 *  strip's and the tabs' drag. One undo step; the order is the print order. */
function reorderSheet(id: string, to: number): void {
  const sheets = drawingSheets();
  const from = sheets.findIndex((s) => s.id === id);
  if (from < 0) return;
  const dest = Math.max(0, Math.min(sheets.length - 1, to));
  if (dest === from) return;
  pushHistory();
  const [moved] = sheets.splice(from, 1);
  sheets.splice(dest, 0, moved);
  renderViewTabs();
  invalidate();
}

/** Drag-to-reorder for a sheet's tab or thumbnail: drop it before or after
 *  another sheet's, along `axis`. */
function makeSheetDraggable(el: HTMLElement, id: string, index: number, axis: 'x'): void {
  el.draggable = true;
  el.dataset.sheetIndex = String(index);
  el.addEventListener('dragstart', (e) => {
    e.dataTransfer?.setData('application/x-stenal-sheet', id);
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
    el.classList.add('dragging');
  });
  el.addEventListener('dragend', () => el.classList.remove('dragging'));
  el.addEventListener('dragover', (e) => {
    if (!e.dataTransfer?.types.includes('application/x-stenal-sheet')) return;
    e.preventDefault();
    const r = el.getBoundingClientRect();
    const after = axis === 'x' ? e.clientX > r.left + r.width / 2 : false;
    el.classList.toggle('drop-before', !after);
    el.classList.toggle('drop-after', after);
  });
  el.addEventListener('dragleave', () => el.classList.remove('drop-before', 'drop-after'));
  el.addEventListener('drop', (e) => {
    const moved = e.dataTransfer?.getData('application/x-stenal-sheet');
    const after = el.classList.contains('drop-after');
    el.classList.remove('drop-before', 'drop-after');
    if (!moved) return;
    e.preventDefault();
    const from = drawingSheets().findIndex((s) => s.id === moved);
    let to = index + (after ? 1 : 0);
    if (from < to) to -= 1; // the dragged sheet leaves a gap before the target
    reorderSheet(moved, to);
  });
}

/** Sheet thumbnails, cached by what they show: the sheet's own content and the
 *  drawing revision (the model its viewports look into). */
const sheetThumbs = new Map<string, { sig: string; url: string }>();
let sheetThumbTimer = 0;
const SHEET_STRIP_KEY = 'stenal.sheetStripCollapsed';

/** The PowerPoint-style strip of sheet thumbnails above the tabs (paper space):
 *  click opens a sheet, double-click renames it, right-click is its menu, drag
 *  reorders. Collapsible; the choice is remembered. */
function renderSheetStrip(): void {
  const strip = $('sheet-strip');
  strip.innerHTML = '';
  let collapsed = false;
  try { collapsed = localStorage.getItem(SHEET_STRIP_KEY) === '1'; } catch { /* storage blocked */ }
  strip.classList.toggle('collapsed', collapsed);
  const toggle = document.createElement('button');
  toggle.className = 'sheet-strip-toggle';
  toggle.textContent = collapsed ? '▴' : '▾';
  toggle.title = tr(collapsed ? 'Show sheet thumbnails' : 'Hide sheet thumbnails');
  toggle.addEventListener('click', () => {
    try { localStorage.setItem(SHEET_STRIP_KEY, collapsed ? '0' : '1'); } catch { /* storage blocked */ }
    renderSheetStrip();
  });
  strip.appendChild(toggle);
  if (collapsed) return;
  drawingSheets().forEach((sh, i) => {
    const card = document.createElement('button');
    card.className = 'sheet-thumb' + (state.viewTab === sh.id ? ' active' : '');
    card.dataset.sheetId = sh.id;
    const frame = document.createElement('div');
    frame.className = 'sheet-thumb-page';
    frame.style.aspectRatio = `${Math.max(1, sh.widthMm)} / ${Math.max(1, sh.heightMm)}`;
    const img = document.createElement('img');
    img.alt = '';
    const cached = sheetThumbs.get(sh.id);
    if (cached) img.src = cached.url;
    frame.appendChild(img);
    const label = document.createElement('span');
    label.className = 'sheet-thumb-name';
    label.textContent = `${i + 1}. ${sheetLabel(sh)}`;
    card.append(frame, label);
    card.addEventListener('click', () => setViewTab(sh.id));
    card.addEventListener('dblclick', () => renameSheet(sh));
    card.addEventListener('contextmenu', (e) => { e.preventDefault(); showSheetTabMenu(e, sh); });
    makeSheetDraggable(card, sh.id, i, 'x');
    strip.appendChild(card);
  });
  const add = document.createElement('button');
  add.className = 'sheet-thumb sheet-thumb-add';
  add.textContent = '＋';
  add.title = tr('Add a sheet');
  add.addEventListener('click', createSheet);
  strip.appendChild(add);
  scheduleSheetThumbs(0);
}

/** Re-render the thumbnails whose sheet or model changed, after a pause. */
export function scheduleSheetThumbs(delay = 700): void {
  if (!document.body.classList.contains('sheet-mode')) return;
  if (sheetThumbTimer) clearTimeout(sheetThumbTimer);
  sheetThumbTimer = window.setTimeout(() => { sheetThumbTimer = 0; void refreshSheetThumbs(); }, delay);
}

async function refreshSheetThumbs(): Promise<void> {
  for (const sh of drawingSheets()) {
    const sig = `${drawingRev()}|${JSON.stringify(sh)}`;
    if (sheetThumbs.get(sh.id)?.sig === sig) continue;
    const dpi = (160 * 25.4) / Math.max(1, sh.widthMm, sh.heightMm);
    const c = await renderSheetToCanvas(sh, dpi);
    if (!c) continue;
    sheetThumbs.set(sh.id, { sig, url: c.toDataURL('image/png') });
    const img = document.querySelector<HTMLImageElement>(`#sheet-strip .sheet-thumb[data-sheet-id="${sh.id}"] img`);
    if (img) img.src = sheetThumbs.get(sh.id)!.url;
  }
}

let renamingSheetId: string | null = null;

/** Begin inline-renaming a sheet tab (Electron has no window.prompt). */
function renameSheet(s: Sheet): void {
  renamingSheetId = s.id;
  if (state.viewTab !== s.id) setViewTab(s.id); // setViewTab re-renders the tabs
  else renderViewTabs();
  const input = document.querySelector<HTMLInputElement>('#view-tabs .view-tab-rename');
  if (input) { input.focus(); input.select(); }
}

/** The in-place text field shown for the sheet being renamed. */
function sheetRenameInput(s: Sheet): HTMLInputElement {
  const input = document.createElement('input');
  input.className = 'view-tab view-tab-rename';
  input.value = s.name ?? '';
  input.placeholder = sheetName({ ...s, name: undefined }, getUiLang(), trIn, roomPlanLabel);
  let done = false;
  const commit = (save: boolean): void => {
    if (done) return;
    done = true;
    renamingSheetId = null;
    // Entered as typed; empty, its default again (docs/claude/names.md).
    const name = input.value.trim();
    if (save && name !== (s.name ?? '')) { pushHistory(); if (name) s.name = name; else delete s.name; }
    renderViewTabs();
  };
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(true); }
    else if (e.key === 'Escape') { e.preventDefault(); commit(false); }
  });
  input.addEventListener('blur', () => commit(true));
  return input;
}

/** Right-click menu on a sheet tab: change paper, orientation and scale. */
function showSheetTabMenu(e: MouseEvent, sh: Sheet): void {
  const menu = $('ctx-menu');
  menu.innerHTML = '';
  const item = (label: string, fn: () => void, opts: { danger?: boolean; check?: boolean } = {}) => {
    const b = document.createElement('button');
    b.textContent = (opts.check ? '✓ ' : '   ') + label;
    if (opts.danger) b.className = 'danger';
    b.addEventListener('click', () => { hideCtxMenu(); fn(); });
    menu.appendChild(b);
  };
  const sep = () => { const d = document.createElement('div'); d.className = 'sep'; menu.appendChild(d); };
  const heading = (t: string) => { const d = document.createElement('div'); d.className = 'ctx-heading'; d.textContent = t; menu.appendChild(d); };

  const setActive = () => { if (state.viewTab !== sh.id) setViewTab(sh.id); };

  heading(tr('Paper'));
  for (const f of ['A0', 'A1', 'A2', 'A3', 'A4'] as const) {
    item(f, () => { pushHistory(); sh.paper = f; applyPaper(sh); }, { check: sh.paper === f });
  }
  sep();
  heading(tr('Orientation'));
  for (const [v, t] of [['landscape', 'Landscape'], ['portrait', 'Portrait']] as const) {
    item(tr(t), () => { pushHistory(); sh.orientation = v; applyPaper(sh); }, { check: sh.orientation === v });
  }
  sep();
  heading(tr('Scale'));
  const uniform = sh.viewports.length > 0 && sh.viewports.every((v) => Math.abs(v.scale - sh.viewports[0].scale) < sh.viewports[0].scale * 0.001) ? sh.viewports[0].scale : null;
  for (const [label, val] of SHEET_SCALES) {
    item(label, () => {
      setActive(); pushHistory();
      for (const v of sh.viewports) v.scale = val;
      renderProperties(); invalidate();
    }, { check: uniform !== null && Math.abs(uniform - val) < val * 0.001 });
  }
  sep();
  item(tr('Rename…'), () => renameSheet(sh));
  item(tr('Duplicate'), () => duplicateSheet(sh));
  item(tr('Delete sheet'), () => deleteSheet(sh.id), { danger: true });

  menu.classList.remove('hidden');
  // Tabs sit at the bottom of the window, so open the menu above the cursor.
  const h = menu.offsetHeight;
  menu.style.left = Math.min(e.clientX, window.innerWidth - 190) + 'px';
  menu.style.top = Math.max(4, Math.min(e.clientY - h, window.innerHeight - h - 4)) + 'px';
}
